import {Inject, Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {IgenomicsState} from '../../core/interfaces/genomics-state.interface';
import {ICurationState} from '../store/curation.reducer';
import {CURATION_ACTIONS} from '../store/curation.actions';
import {IcurationWorklistItem} from '../interfaces/curation-worklist-item.interface';
import {CurationService} from './curation.service';
import {Observable} from 'rxjs/Observable';
import {ICurationItem} from '../interfaces/curation-item.interface';
import {ICurationCreateRequest} from '../interfaces/curation-create-request.interface';
import {ICurationUpdateRequest} from '../interfaces/curation-update-request.interface';
import {CurationUtils} from '../utils/curation-utils';
import {IRevision} from '../../core/interfaces/workflow.interface';
import {CurationSettings} from '../components/curation-dashboard/curation-setting';
import {BehaviorSubject} from 'rxjs';
import {
  selectActiveJobInputValueByKey,
  selectActivePractitionerReferenceByType,
  selectActiveReferencesByPractType,
  selectActiveReferencesValueByKey
} from "@ispm/workflow/store/workflow-config.selectors";
import {cloneDeep, get, has} from 'lodash';
import {
  IGrantedAction,
  IGrantedActionsTable,
  IMultipleKbData
} from "../../worklist/interfaces/granted-actions-table.interface";
import {WorkflowService} from "../../workflow-config/services/workflow.service";
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import {take} from 'rxjs/operators';

@Injectable()
export class CurationActionCreatorService {
  private curationStore: Store<ICurationState>;
  private DBName: string;
  public activeCurationSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  private lastEditorAction: string = "";

  constructor(private store: Store<IgenomicsState>,
              private curationService: CurationService,
              private curationUtil: CurationUtils,
              private workflowService: WorkflowService,
              private authService: AuthenticationService,
              private wfService: WorkflowService) {
    this.curationStore = store.select('curation') as any;
  }

  getLastEditorAction() {
    return this.lastEditorAction;
  }

  setLastEditorAction(action) {
    this.lastEditorAction = action;
  }

  public getRevisionOpinions(revisions: Array<{
    revision: IRevision,
    item: { [prop: string]: any }
  }>, sortParamName?: string, sortParamValue?: string): Observable<Array<any>> {
    return this.wfService.getRevisionOpinions(revisions, sortParamName, sortParamValue);
  }

  public initState() {
    this.curationStore.dispatch({
        type: CURATION_ACTIONS.INIT,
      }
    );
  }

  public getState(props: Array<keyof ICurationState> = []) {
    let state: any = this.curationStore;
    for (const prop of props) {
      state = state.select(prop);
    }
    return state;
  }

  public setStateActiveKB(activeKB: string) {
    this.curationStore.dispatch({
        type: CURATION_ACTIONS.SET_CURATION_ACTIVE_KB,
        payload: activeKB,
      }
    );
  }

  public setStateGrantedKB(grantedKB: string) {
    this.curationStore.dispatch({
        type: CURATION_ACTIONS.SET_CURATION_GRANTED_KB,
        payload: grantedKB
      }
    );
  }

  public setStateMultipleKBData(mulpleKbData: IMultipleKbData) {
    this.curationStore.dispatch({
        type: CURATION_ACTIONS.SET_MULTIPLE_KB_DATA,
        payload: mulpleKbData
      }
    );
  }

  public setStateGrantedActionsTable(grantedActionsTable: Array<keyof IGrantedAction>) {
    this.curationStore.dispatch({
        type: CURATION_ACTIONS.SET_GRANTED_ACTIONS_TABLE,
        payload: grantedActionsTable
      }
    );
  }

  public getMultiStepReviewFlag(): Observable<string> {
    return this.getCurrentStepInputByKey("multiStepReview");
  }

  public getAssignedPractioners(): Observable<string[]> {
    return this.store.pipe(
      select(selectActiveReferencesByPractType('performer'))
    );
  }

  private getCurrentStepInputByKey(key: string): Observable<string> {
    return this.store.pipe(
      select(selectActiveJobInputValueByKey(key))
    );
  }

  private getReferenceValueByKey(key: string): Observable<string> {
    return this.store.pipe(
      select(selectActiveReferencesValueByKey(key))
    );
  }

  public getCurationSearchConfigParams(): any {
    // Return curation external search config. Located at curationDefinition/externalSearch
    const configParams: any = {};
    this.getState(['definitions'])
      .pipe(take(1))
      .subscribe((data => {
        const conf = Array.from(data.values()).find(data => data['type'] === 'aberration');
        if (conf) {
          // if (!conf['ui']['forms']['externalSearch']) {
          //   console.error('CurationActionCreatorService->getCurationSearchConfigParams: No externalSearch was found');
          //   return;
          // }
          // Object.assign(configParams, conf['ui']['forms']['externalSearch']);
        } else {
          console.error('CurationActionCreatorService->getCurationSearchConfigParams: Cannot find configuration');
        }
      }));
    return configParams;
  }

  public setCurationWorklistValuesets(valuesetIds: string[]): Observable<any> {
    return this.curationService.getCurationWorklistValuesets(valuesetIds)
      .map((valuesets: Array<any>) => {
        this.store.dispatch({
          type: CURATION_ACTIONS.SET_CURATION_VALUESETS,
          payload: valuesets
        });
      });
  }

  public setCurationValuesets(valuesets: Array<any>) {
    this.store.dispatch({
      type: CURATION_ACTIONS.SET_CURATION_VALUESETS,
      payload: valuesets
    });
  }

  public setCurationDefinition(params?: string): Observable<any> {
    return this.curationService.getCurationDefinition(params)
      .map((definitions: Array<any>) => {
        this.curationStore.dispatch({
          type: CURATION_ACTIONS.SET_CURATION_DEFINITION,
          payload: definitions
        });
        const filteredItem = definitions.filter(data => data.resource['type'] === CurationSettings.CURATION_KB)[0];
        this.setStateActiveKB(filteredItem.resource.id);
        if (has(filteredItem.resource, CurationSettings.MULTIPLE_KB)) {
          const multipleKbData = get(filteredItem.resource, CurationSettings.MULTIPLE_KB);
          this.setStateMultipleKBData(cloneDeep(multipleKbData));
        } else {
          const grantedKbName = get(filteredItem.resource, CurationSettings.CURATION_GRANTED_KB, undefined);
          this.setStateGrantedKB(grantedKbName);
        }
      });
  }

  findGrantedActionsTable(conf: any) {
    return get(conf, CurationSettings.MULTIPLE_KB + '.' + CurationSettings.GRANTED_ACTIONS_TABLE, undefined);
  }

  public getCurationWorkList(curationType: string, conf: any, filter: string): Observable<IcurationWorklistItem[]> {
    let context = this.getContextByTypeAndConf(curationType, conf);
    if (filter && filter.includes(CurationSettings.CURATION_ORGANIZATION)) {
      [context, filter] = this.updateOrganizationFilter(context, filter);
    }
    return this.curationService.getCurationWorkList(context, curationType, conf, filter);
  }

  private updateOrganizationFilter(context: string, filter: string): string[] {
    const filterObj = JSON.parse(filter);
    const filterObjOrganizations = filterObj.organization.split(",");
    const splitContext = context.split(",");
    if (splitContext.length > 1) {
      context = splitContext.filter(val => !!val && filterObjOrganizations.includes(val)).join(",");
    }
    delete filterObj.organization;
    filter = JSON.stringify((filterObj));
    return [context, filter];
  }

  private getContextByTypeAndConf(curationType: string, conf: any): string {
    let useAuthAsKb = !!conf && !!this.curationService ? get(conf, 'ui.worklist.useAuthAsKb', false) : true;
    return useAuthAsKb ? this.getUserAuthorization() : this.getDb();
  }

  public createCurationItem(data: ICurationCreateRequest): Observable<any> {
    return this.curationService.createCurationItem(this.getDb(true), data);
  }

  public createCurationItemV1(data: ICurationCreateRequest): Observable<any> {
    return this.curationService.createCurationItemV1(this.getDb(true), data, this.getType(data));
  }

  public getCurationItemByKeys(data: ICurationCreateRequest): Observable<any> {
    return this.curationService.getCurationItemByKeys(this.getDb(), data);
  }

  public searchCurationInfoWithFilter(data: any, forCurrentOrg: boolean = false): Observable<any> {
    return this.curationService.searchCurationInfo(this.getDb(forCurrentOrg), data.type, data.query);
  }

  public getCurationItemByKeysV1(data: any, forCurrentOrg: boolean = false): Observable<any> {
    return this.curationService.getCurationItemByKeysV1(this.getDb(forCurrentOrg), data, this.getType(data));
  }

  public getCurationItemV1(curationId: string, revisionId: string, type: string, toggleLoader: boolean = true): Observable<ICurationItem> {
    return this.curationService.getCurationItemV1(curationId, revisionId, this.getDb(), type, toggleLoader);
  }

  public getCurationItem(curationId: string, revisionId: string): Observable<ICurationItem> {
    return this.curationService.getCurationItem(curationId, revisionId, this.getDb());
  }

  public updateCurationItem(curationId: string, data: ICurationUpdateRequest): Observable<any> {
    return this.curationService.updateCurationItem(curationId, data, this.getDb(true));
  }

  public updateCurationItemV1(curationId: string, data: ICurationUpdateRequest, type: string, status: string): Observable<any> {
    return this.curationService.updateCurationItemV1(curationId, data, this.getDb(true), type, status);
  }

  public discardCurrentRevision(curationId: string, data: ICurationUpdateRequest, type: string, status: string): Observable<any> {
    return this.curationService.discardCurrentRevision(curationId, data, this.getDb(true), type, status);
  }

  public updateCurationWhiteListItemV1(curationId: string, data: ICurationUpdateRequest, type: string): Observable<any> {
    return this.curationService.updateCurationWhiteListItemV1(curationId, data, this.getDb(true), type);
  }

  public getRelatedEntities(curationId: string, available?: boolean): Observable<any> {
    return this.curationService.getRelatedEntities(curationId, this.getDb(), available);
  }

  public updateHasNotes(curationInfoId: string, type: string): Observable<any> {
    return this.curationService.updateHasNotes(curationInfoId, this.getDb(true), type);
  }

  public cloneCurationItem(curationId: string, revisionId: string, disease: string, opinion: any): Observable<any> {
    return this.curationService.cloneCurationItem(this.getDb(true), curationId, revisionId, disease, opinion);
  }

  public listCurationNames(): Observable<any> {
    return this.curationService.listCurationNames(this.getDb());
  }

  public getOwnerId(): Observable<string> {
    return this.store.pipe(
      select(selectActivePractitionerReferenceByType('orderer'))
    );
  }

  public reassignCurationItem(curationItemId: string, revisionId: string, workflowRequestId: string, ownerId: string,
                              type: string, kb: string, status: string) {
    return this.workflowService.reassignCurationItem({
      activeRevision: revisionId,
      workflowrequest_id: workflowRequestId,
      curationItemID: curationItemId,
      type,
      kb,
      status
    }, ownerId);
  }

  public getCurationRevisions(workflowRequestId: string): Observable<Array<{
    revision: IRevision,
    item: { [prop: string]: any }
  }>> {
    const workflowReq = this.curationService.getWorkflowRequest(workflowRequestId)
      .catch((error: any) => {
        if (error.status == 500) {
          console.error('Error workflowService.getWorkflowRequest: status 500');
        }
        return Observable.of({});
      }).publish();
    workflowReq.connect();
    return workflowReq.map((response: any) => {
      return response.revisions.map(revision => {
        return {revision: revision};
      });
    });
  }

  private getUserAuthorization() {
    return this.authService.getUser().organization;
  }

  public getDb(createCuration?: boolean) {
    this.getState(['definitions']).pipe(take(1))
      .subscribe((data => {
        const conf = Array.from(data.values()).filter(dataTemp => dataTemp['type'] === 'curationKB')[0];
        this.DBName = this.getActiveKB();
        // support previous version for small organization, which has only 'grantedKb' descriptor string
        if (has(conf, CurationSettings.CURATION_GRANTED_KB)) {
          this.DBName = get(conf, CurationSettings.CURATION_GRANTED_KB);
        }
        // big organizations with full 'multipleKb' descriptor
        else {
          const multipleKbPrefix = CurationSettings.MULTIPLE_KB + '.' + CurationSettings.CURATION_GRANTED_KB;
          if (has(conf, multipleKbPrefix)) {
            this.DBName = !createCuration ? get(conf, multipleKbPrefix) : this.getActiveKB();
          }
        }
      }));
    return this.DBName;
  }

  public getActiveKB() {
    let activeKB: string;
    this.getState(['activeKnowledgeBase']).pipe(take(1))
      .subscribe(value => {
        activeKB = value;
      });
    return activeKB;
  }

  public getGrantedKB() {
    let grantedKB: string;
    this.getState(['multipleKbData']).pipe(take(1))
      .subscribe(value => {
        grantedKB = value.grantedKnowledgeBase;
      });
    return grantedKB;
  }

  public getGrantedActionsTable() {
    let grantedActionsTable: IGrantedActionsTable;
    this.getState(['multipleKbData']).pipe(take(1))
      .subscribe(value => {
        grantedActionsTable = value.grantedActionsTable;
      });
    return grantedActionsTable;
  }

  private getType(cReq: any) {
    return cReq['content']['type'];
  }

  public updateActiveCuration(curationId: string): void {
    this.activeCurationSubject.next(curationId);
  }

  public getActiveCurationObservable(): Observable<string> {
    return this.activeCurationSubject.asObservable();
  }

  public getDownloadedFile(fileId: string): Observable<any> {
    return this.curationService.downloadFile(fileId);
  }

  public getCurationItemRevisions(curationItemId: string, queryParams: { [key: string]: string }): Observable<any> {
    queryParams['context'] = curationItemId;
    return this.curationService.getCurationDataList(queryParams).map(res => res.body);
  }

  public setCurationItemOwner(curationItemId: string, revisionId: string, workflow: string, ownerId: string,
                              type: string, status: string): Observable<any> {
    return this.curationService.setCurationItemOwner(this.getDb(true), curationItemId, revisionId, workflow, ownerId,
      type, status);
  }

  submitCurationItemForReview(curationItemId: string, revisionId: string, workflow: string,
                              activePractitionerId: string, reviewer: string, type: string,
                              status: string): Observable<any> {
    return this.curationService.submitCurationItemForReview(this.getDb(true), curationItemId, revisionId, workflow,
      activePractitionerId, reviewer, type, status);
  }
}
